import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import vacanciesService from '../services/vacancies.service';
import ReferrerBadge from '../components/ReferrerBadge';
import ChatPanel from '../components/Chat/ChatPanel';
import { ReferralSubmission } from '../types/ReferralTypes';
import { ReferMeRequest } from '../types/ReferMeTypes';
import { ReferrerRating, getBadgeLevel } from '../types/RatingTypes';
import { formatCurrency, timeAgo, getStatusColor } from '../utils/formatters';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

interface MyReferralsProps {
  darkMode: boolean;
}

const MyReferrals: React.FC<MyReferralsProps> = ({ darkMode }) => {
  const { isAuthenticated } = useAuth();
  const [referrals, setReferrals] = useState<ReferralSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [chatReferral, setChatReferral] = useState<ReferralSubmission | null>(null);
  const [activeTab, setActiveTab] = useState<'submitted' | 'posted' | 'refer_me'>('submitted');
  const [referMeRequests, setReferMeRequests] = useState<ReferMeRequest[]>([]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const data = await vacanciesService.getMyReferrals();
      setReferrals(data);
      // Load refer-me requests from localStorage
      const stored = localStorage.getItem('refer_me_requests');
      if (stored) setReferMeRequests(JSON.parse(stored));
      setLoading(false);
    };
    if (isAuthenticated) fetch();
    else setLoading(false);
  }, [isAuthenticated]);

  const computeRating = (): ReferrerRating => {
    const total = referrals.length;
    const hires = referrals.filter(r => r.status === 'hired' || r.status === 'bounty_paid').length;
    const rate = total > 0 ? Math.round((hires / total) * 100) : 0;
    const earned = referrals
      .filter(r => r.status === 'bounty_paid')
      .reduce((sum, r) => sum + r.bounty_amount, 0);
    return {
      total_referrals: total,
      successful_hires: hires,
      success_rate: rate,
      total_earned: earned,
      badge: getBadgeLevel(total, rate),
    };
  };

  if (!isAuthenticated) {
    return (
      <div className={`page-container ${darkMode ? 'dark' : ''}`}>
        <div className="empty-state">
          <h3>Sign in to view your referrals</h3>
          <p>Track the status of candidates you have referred.</p>
        </div>
      </div>
    );
  }

  const rating = computeRating();

  return (
    <div className={`page-container ${darkMode ? 'dark' : ''}`}>
      <h1>My Referrals</h1>
      <p className="page-subtitle">Track the candidates you have referred and their progress.</p>

      {/* Stats Card */}
      {!loading && referrals.length > 0 && (
        <div className="referral-stats-card">
          <div className="stats-row">
            <div className="stat-item">
              <span className="stat-item-value">{rating.total_referrals}</span>
              <span className="stat-item-label">Total Referrals</span>
            </div>
            <div className="stat-item">
              <span className="stat-item-value">{rating.successful_hires}</span>
              <span className="stat-item-label">Successful Hires</span>
            </div>
            <div className="stat-item">
              <span className="stat-item-value">{rating.success_rate}%</span>
              <span className="stat-item-label">Success Rate</span>
            </div>
            <div className="stat-item">
              <span className="stat-item-value">{formatCurrency(rating.total_earned)}</span>
              <span className="stat-item-label">Total Earned</span>
            </div>
          </div>
          <div className="stats-badge-row">
            <ReferrerBadge badge={rating.badge} />
            {rating.badge !== 'elite' && (
              <span className="stats-hint">
                <TrendingUpIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                {' '}Keep referring to level up!
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="referrals-tabs">
        <button
          className={activeTab === 'submitted' ? 'active' : ''}
          onClick={() => setActiveTab('submitted')}
        >
          My Referrals
        </button>
        <button
          className={activeTab === 'posted' ? 'active' : ''}
          onClick={() => setActiveTab('posted')}
        >
          My Posted Vacancies
        </button>
        <button
          className={activeTab === 'refer_me' ? 'active' : ''}
          onClick={() => setActiveTab('refer_me')}
        >
          Refer Me Requests {referMeRequests.length > 0 && `(${referMeRequests.length})`}
        </button>
      </div>

      {activeTab === 'submitted' && (
        <>
          {loading ? (
            <div className="loading-state">Loading referrals...</div>
          ) : referrals.length === 0 ? (
            <div className="empty-state">
              <h3>No referrals yet</h3>
              <p>Browse vacancies and start referring great candidates to earn bounties.</p>
            </div>
          ) : (
            <div className="referrals-list">
              {referrals.map(r => (
                <div key={r.id} className="referral-item">
                  <div className="referral-item-header">
                    <div>
                      <h3>{r.candidate_name}</h3>
                      <p className="referral-vacancy">
                        {r.vacancy_title} at {r.company_name}
                      </p>
                    </div>
                    <div className="referral-item-right">
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(r.status) }}
                      >
                        {r.status.replace('_', ' ')}
                      </span>
                      <span className="referral-bounty">
                        {formatCurrency(r.bounty_amount)}
                      </span>
                    </div>
                  </div>
                  <div className="referral-item-meta">
                    <span>{r.candidate_email}</span>
                    {r.candidate_linkedin && <span>{r.candidate_linkedin}</span>}
                    <span>Submitted {timeAgo(r.created_at)}</span>
                  </div>
                  {r.note && <p className="referral-note">{r.note}</p>}
                  <div className="referral-item-actions">
                    <button
                      className="btn-outline btn-sm"
                      onClick={() => setChatReferral(r)}
                    >
                      <ChatBubbleOutlineIcon sx={{ fontSize: 16, marginRight: '4px' }} />
                      Message Poster
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'posted' && (
        <div className="empty-state">
          <h3>Coming soon</h3>
          <p>Manage referrals received on your posted vacancies from the vacancy detail page.</p>
        </div>
      )}

      {activeTab === 'refer_me' && (
        <>
          {loading ? (
            <div className="loading-state">Loading requests...</div>
          ) : referMeRequests.length === 0 ? (
            <div className="empty-state">
              <PersonSearchIcon sx={{ fontSize: 48, color: '#4f46e5', marginBottom: '12px' }} />
              <h3>No referral requests yet</h3>
              <p>Browse vacancies and click "Get Referred" to request a referral from verified employees.</p>
            </div>
          ) : (
            <div className="referrals-list">
              {referMeRequests.map(r => (
                <div key={r.id} className="referral-item">
                  <div className="referral-item-header">
                    <div>
                      <h3>{r.vacancy_title}</h3>
                      <p className="referral-vacancy">{r.company_name}</p>
                    </div>
                    <div className="referral-item-right">
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor:
                            r.status === 'pending' ? '#f59e0b'
                            : r.status === 'matched' ? '#3b82f6'
                            : r.status === 'referred' ? '#22c55e'
                            : '#94a3b8',
                        }}
                      >
                        {r.status}
                      </span>
                    </div>
                  </div>
                  <div className="referral-item-meta">
                    {r.matched_referrer_name && (
                      <span>Referrer: {r.matched_referrer_name}</span>
                    )}
                    <span>Submitted {timeAgo(r.created_at)}</span>
                  </div>
                  {r.pitch && <p className="referral-note">{r.pitch}</p>}
                  {r.skills.length > 0 && (
                    <div className="candidate-skills-chips" style={{ marginTop: '8px' }}>
                      {r.skills.map((s, i) => (
                        <span key={i} className="skill-chip">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {chatReferral && (
        <ChatPanel
          referralId={chatReferral.id}
          vacancyTitle={chatReferral.vacancy_title}
          candidateName={chatReferral.candidate_name}
          isOpen={!!chatReferral}
          onClose={() => setChatReferral(null)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

export default MyReferrals;
